/**
 * What the wallets you follow have done in this token lately.
 *
 * The rail's own panel, and the one thing on this page neither terminal we are
 * measured against can show: they have no idea whose wallet a maker is.
 *
 * It answers one question first — is the money you respect going in or out —
 * and only then lists who. That order matters: a column of addresses is a
 * lookup, a single signed figure is a decision.
 */

export type TrackedTrade = {
  maker: string
  isBuy: boolean
  totalUsd: number
  at: number
}

export type TrackedRow = {
  maker: string
  isBuy: boolean
  totalUsd: number
  at: number
}

/**
 * What a wallet is left holding of this token, when we happen to know.
 *
 * It carries the address as the chain spelled it, because base58 is
 * case-sensitive and a lowercased copy is a different string: it would not
 * open the wallet it names and would not paste into an explorer.
 */
export type Holding = {
  address: string
  heldTokens: number
  heldUsd: number
}

export type TrackedSummary = {
  netUsd: number
  buying: number
  selling: number
  rows: ReadonlyArray<TrackedRow>
}

/** An hour, because it is the window a tape of this length actually covers. */
const WINDOW_MS = 60 * 60 * 1000

export const trackedSummary = (
  trades: ReadonlyArray<TrackedTrade>,
  {
    now = Date.now(),
    windowMs = WINDOW_MS,
    rows: rowLimit = 6,
  }: { now?: number; windowMs?: number; rows?: number } = {},
): TrackedSummary => {
  const recent = trades.filter(
    (trade) => Number.isFinite(trade.totalUsd) && now - trade.at <= windowMs,
  )

  let netUsd = 0
  const buyers = new Set<string>()
  const sellers = new Set<string>()
  /* One row per wallet: six trades by one wallet is one wallet's opinion, and
     a panel that repeats it six times reads as six people agreeing. */
  const latest = new Map<string, TrackedRow>()

  for (const trade of recent) {
    netUsd += trade.isBuy ? trade.totalUsd : -trade.totalUsd
    ;(trade.isBuy ? buyers : sellers).add(trade.maker)

    const held = latest.get(trade.maker)
    if (!held || trade.at > held.at) {
      latest.set(trade.maker, { ...trade })
    }
  }

  return {
    netUsd,
    buying: buyers.size,
    selling: sellers.size,
    rows: [...latest.values()]
      .sort((left, right) => right.at - left.at)
      .slice(0, rowLimit),
  }
}

/**
 * What each wallet is left holding, indexed by address.
 *
 * The holders read answers for the top of the book only, so a followed wallet
 * that holds a little will not be in it. That is a real "we do not know", not
 * a zero, and the difference matters: a panel that prints $0 for a wallet
 * still holding is lying, and one that prints a dash is not.
 */
export const holdingsByAddress = (
  makers: ReadonlyArray<{
    address: unknown
    balanceCurrency: { toNumber: () => number }
    balanceInUsd: { toNumber: () => number }
  }>,
): Map<string, Holding> => {
  const held = new Map<string, Holding>()

  for (const maker of makers) {
    const address = String(maker.address)
    held.set(address.toLowerCase(), {
      address,
      heldTokens: maker.balanceCurrency.toNumber(),
      heldUsd: maker.balanceInUsd.toNumber(),
    })
  }

  return held
}

/** Hangs a holding on each row, where one is known. */
export const withHoldings = <T extends { maker: string }>(
  rows: ReadonlyArray<T>,
  holdings: Map<string, Holding>,
): ReadonlyArray<T & { holding: Holding | undefined }> =>
  rows.map((row) => ({
    ...row,
    holding: holdings.get(row.maker.toLowerCase()),
  }))

export type TrackedHolder = {
  maker: string
  holding: Holding
  /** Their most recent move in this token, when the tape has seen one. */
  last: { isBuy: boolean; totalUsd: number; at: number } | undefined
}

/**
 * The followed wallets that are in this token, ranked by how much.
 *
 * Holding is the fact worth leading with. An earlier version listed only the
 * wallets that had traded in the last hour, which on a quiet token is nobody —
 * and "nobody traded recently" was being shown where "two of the wallets you
 * follow are sitting on this" was the truth.
 *
 * The last move is hung on where the tape happens to have seen one. Its
 * absence means the tape has not seen it, not that the wallet is idle.
 */
export const trackedHolders = (
  holdings: Map<string, Holding>,
  tracked: Iterable<string>,
  recent: ReadonlyArray<TrackedTrade> = [],
  { limit = 6 }: { limit?: number } = {},
): ReadonlyArray<TrackedHolder> => {
  const lastByMaker = new Map<string, TrackedTrade>()
  for (const trade of recent) {
    const key = trade.maker.toLowerCase()
    const held = lastByMaker.get(key)
    if (!held || trade.at > held.at) {
      lastByMaker.set(key, trade)
    }
  }

  return [...tracked]
    .map((address) => holdings.get(address.toLowerCase()))
    .filter(
      (holding): holding is Holding =>
        holding !== undefined && holding.heldTokens > 0,
    )
    .sort((left, right) => right.heldUsd - left.heldUsd)
    .slice(0, limit)
    .map((holding) => {
      const last = lastByMaker.get(holding.address.toLowerCase())
      return {
        /* The holders read is the authority on spelling: it is where this
           address came off the chain. */
        maker: holding.address,
        holding,
        last: last
          ? { isBuy: last.isBuy, totalUsd: last.totalUsd, at: last.at }
          : undefined,
      }
    })
}
